/**
 * Bank Balance chart (per-bank lines): a BLACK "Total" consolidation line
 * plus one colored line per bank, Jan-Dec of the current calendar year, no
 * forecast (future/unpopulated months render as a gap, not a fabricated
 * zero -- see lib/entityBankBalanceHistoryChart.js). Visual model: the
 * Occupancy chart's black "All" + colored region lines
 * (pages/PropertyPortfolioPage.jsx), extended to an open-ended bank list the
 * same way pages/BROSDashboardPnlTab.jsx's per-client lines do -- a colored
 * line's value label only appears at its own last populated point (not
 * every point), which stays legible regardless of how many banks are in
 * scope; the black Total line is drawn last (on top) and heavier.
 *
 * Shared by the entity/Personal dashboard (and its All Entities/All Personal
 * aggregates), the Overall dashboard (group scope), and the Banking page --
 * all consume the same scope-parameterized bank-balance-history series, so
 * no surface can disagree with another on a given month's total or a given
 * bank's total.
 *
 * Partial coverage: a month's Total point is hollow when ANY account in
 * scope lacks that month's history (row._partial); a bank's own line point
 * is hollow only when one of THAT bank's accounts lacks it (row._bankPartial),
 * independent of every other bank's coverage that month.
 */
import React from 'react';
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, LabelList, Text,
} from 'recharts';
import { AlertTriangle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { formatCompact, formatCurrencyFull } from '../../lib/chartFormatters';
import { latestPopulatedRow, insufficientMonthLabels } from '../../lib/entityBankBalanceHistoryChart';

// Black is reserved for the Total/consolidation line only (never assigned to
// a bank -- lib/entityBankBalanceHistoryChart.js's collectBankSeries never
// hands out this exact value), matching the "All" convention on the
// Occupancy chart and the per-client "Overall Avg" line on BROSDashboardPnlTab.
const TOTAL_COLOR = '#000000';

function lastPopulatedIndex(rows, key) {
  for (var i = rows.length - 1; i >= 0; i--) {
    if (rows[i][key] != null) return i;
  }
  return -1;
}

// Hollow ring over a month where the Total (whole-scope) figure is partial --
// same visual language components/dashboard/group/GroupOverviewTab.jsx's
// CashDevelopmentChart established for the jurisdiction version of this chart.
function totalHollowDot(rows) {
  return function (props) {
    var cx = props.cx, cy = props.cy, index = props.index;
    var row = rows[index];
    if (!row || !row._partial || cx == null || cy == null) return null;
    return <circle cx={cx} cy={cy} r={4} fill="hsl(var(--background))" stroke={TOTAL_COLOR} strokeWidth={2} />;
  };
}

// Hollow ring for ONE bank's line: only when that specific bank's own
// accounts are the ones missing this month, not whenever any other bank in
// scope is partial.
function bankHollowDot(rows, bankKey, color) {
  return function (props) {
    var cx = props.cx, cy = props.cy, index = props.index;
    var row = rows[index];
    if (!row || !row._bankPartial || !row._bankPartial[bankKey] || cx == null || cy == null) return null;
    return <circle cx={cx} cy={cy} r={4} fill="hsl(var(--background))" stroke={color} strokeWidth={2} />;
  };
}

// Value label only at a line's own last populated point -- with an
// open-ended number of bank lines sharing one chart, labeling every point on
// every line is unreadable (BROSDashboardPnlTab's per-client lines use the
// same last-point-only convention for the same reason).
function lastPointLabelContent(rows, key, color, formatter) {
  var lastIdx = lastPopulatedIndex(rows, key);
  return function (props) {
    var x = props.x, y = props.y, index = props.index, value = props.value;
    if (value == null || index !== lastIdx) return null;
    return (
      <Text x={x} y={y - 8} textAnchor="middle" verticalAnchor="end" fontSize={11} fontWeight={700} fill={color}>
        {formatter(value)}
      </Text>
    );
  };
}

export function BankBalanceHistoryChart(props) {
  var rows = props.rows || [];
  var series = props.series || [];
  var displayCurrency = props.displayCurrency || 'GBP';
  var unknownBankAccounts = props.unknownBankAccounts || [];

  var insufficient = insufficientMonthLabels(rows);
  var hasAnyData = rows.some(function (r) {
    return r._total != null || series.some(function (s) { return r[s.key] != null; });
  });
  if (!hasAnyData) return null;

  var latest = latestPopulatedRow(rows);
  var hasPartialCoverage = rows.some(function (r) {
    return r._partial || series.some(function (s) { return r._bankPartial && r._bankPartial[s.key]; });
  });

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-lg">Bank Balance</CardTitle>
        {insufficient.length > 0 && (
          <p className="text-xs text-muted-foreground mt-0.5" data-testid="bank-balance-insufficient-note">
            No total for {insufficient.join(', ')}: not enough data, some accounts have no balance
            history for {insufficient.length === 1 ? 'that month' : 'those months'}.
          </p>
        )}
        {hasPartialCoverage && (
          <p className="text-xs text-amber-600 mt-0.5">
            Hollow markers: partial coverage -- one or more accounts (for that bank, or overall) had not yet started
            their transaction history that month, so the figure is real but understated. Hover for detail.
          </p>
        )}
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={340}>
          <LineChart data={rows} margin={{ top: 24, right: 10, left: 0, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} opacity={0.3} />
            <XAxis dataKey="month" tick={{ fontSize: 12 }} />
            <YAxis tickFormatter={function (v) { return formatCompact(v, displayCurrency); }} tick={{ fontSize: 11 }} width={60} />
            <Tooltip
              formatter={function (val, name, entry) {
                var row = entry && entry.payload;
                var dataKey = entry && entry.dataKey;
                if (dataKey === '_total') {
                  if (!row || !row._partial) return [formatCurrencyFull(val, displayCurrency), 'Total'];
                  var excluded = row._excludedAccounts || [];
                  var shown = excluded.slice(0, 3).join(', ');
                  var more = excluded.length > 3 ? ' +' + (excluded.length - 3) + ' more' : '';
                  var suffix = excluded.length ? ' (partial -- excludes ' + shown + more + ')' : ' (partial)';
                  return [formatCurrencyFull(val, displayCurrency) + suffix, 'Total'];
                }
                var isPartial = row && row._bankPartial && row._bankPartial[dataKey];
                if (!isPartial) return [formatCurrencyFull(val, displayCurrency), name];
                var bankExcluded = (row._bankExcludedAccounts && row._bankExcludedAccounts[dataKey]) || [];
                var bankShown = bankExcluded.slice(0, 3).join(', ');
                var bankMore = bankExcluded.length > 3 ? ' +' + (bankExcluded.length - 3) + ' more' : '';
                var bankSuffix = bankExcluded.length ? ' (partial -- excludes ' + bankShown + bankMore + ')' : ' (partial)';
                return [formatCurrencyFull(val, displayCurrency) + bankSuffix, name];
              }}
            />
            {series.map(function (s) {
              return (
                <Line key={s.key} type="monotone" dataKey={s.key} name={s.label} stroke={s.color}
                  strokeWidth={1.5} connectNulls={false} dot={bankHollowDot(rows, s.key, s.color)}>
                  <LabelList content={lastPointLabelContent(rows, s.key, s.color, function (v) { return formatCompact(v, displayCurrency); })} />
                </Line>
              );
            })}
            <Line type="monotone" dataKey="_total" name="Total" stroke={TOTAL_COLOR} strokeWidth={2.5}
              connectNulls={false} dot={totalHollowDot(rows)}>
              <LabelList content={lastPointLabelContent(rows, '_total', TOTAL_COLOR, function (v) { return formatCompact(v, displayCurrency); })} />
            </Line>
          </LineChart>
        </ResponsiveContainer>
        {latest && series.length > 0 && (
          <div className="mt-2 text-sm text-muted-foreground flex items-center gap-4 flex-wrap">
            <span>Latest ({latest.month}):</span>
            {series.map(function (s) {
              return (
                <span key={s.key} className="inline-flex items-center gap-1">
                  <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ backgroundColor: s.color }} />
                  <span>{s.label}</span>
                  <span className="font-mono font-semibold text-foreground">
                    {formatCompact(latest[s.key], displayCurrency)}
                  </span>
                </span>
              );
            })}
            <span className="inline-flex items-center gap-1">
              <span className="inline-block w-2.5 h-2.5 rounded-full bg-foreground" />
              <span>Total</span>
              <span className="font-mono font-semibold text-foreground">
                {formatCompact(latest._total, displayCurrency)}
              </span>
            </span>
          </div>
        )}
        {unknownBankAccounts.length > 0 && (
          <div
            className="mt-2 flex items-center gap-1.5 rounded-md bg-amber-50 dark:bg-amber-950 border border-amber-200 dark:border-amber-800 p-3 text-sm text-amber-800 dark:text-amber-200"
            data-testid="bank-balance-unknown-accounts-warning"
          >
            <AlertTriangle className="h-4 w-4 shrink-0" />
            {unknownBankAccounts.length} account(s) with no institution on file, grouped under "Unknown": {unknownBankAccounts.join(', ')}.
          </div>
        )}
      </CardContent>
    </Card>
  );
}
